import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MapPin } from "lucide-react";
import {
  resolveTrackHubLocation,
  reverseTrackHubLocation,
} from "../../services/trackHubApi";
import DropoffLocationMap from "./DropoffLocationMap";
import "./dropoff-location-field.css";

const REVERSE_DEBOUNCE_MS = 800;

function parseCoord(value) {
  const s = String(value ?? "").trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

function coordsMatch(aLat, aLng, bLat, bLng) {
  return Math.abs(aLat - bLat) < 1e-6 && Math.abs(aLng - bLng) < 1e-6;
}

/**
 * Customer location field: maps link, Plus Code, coordinates, or a written address,
 * with resolve and a draggable pin map.
 *
 * value: { query, lat, lng, plusCode, nationalShortCode, address }
 * onChange(patch, { source }) — patch contains only the keys that changed.
 *   source is "input" | "resolve" | "map" | "reverse".
 * mapFocus: { lat, lng, zoom, token } pans the map without reverse-geocoding.
 */
function DropoffLocationField({
  value,
  onChange,
  active = true,
  disabled = false,
  showHeading = true,
  heading,
  label,
  placeholder,
  hint,
  mapHint,
  mapFocus = null,
  onBusyChange,
  className = "",
  chips = null,
  onChip,
}) {
  const { t, i18n } = useTranslation("translation");
  const queryId = useId();
  const [resolving, setResolving] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [resolveStatus, setResolveStatus] = useState(null);
  const [mapTarget, setMapTarget] = useState(null);
  const [seenActive, setSeenActive] = useState(active);

  const onChangeRef = useRef(onChange);
  const onBusyChangeRef = useRef(onBusyChange);
  const skipPanRef = useRef(false);
  const focusRef = useRef(null);
  const reverseTimerRef = useRef(null);
  const reverseSeqRef = useRef(0);

  onChangeRef.current = onChange;
  onBusyChangeRef.current = onBusyChange;

  if (active !== seenActive) {
    setSeenActive(active);
    if (active) {
      setLocationError(null);
      setResolveStatus(null);
      setMapTarget(null);
    }
  }

  const query = value?.query ?? "";
  const latValue = value?.lat ?? "";
  const lngValue = value?.lng ?? "";

  useEffect(() => {
    onBusyChangeRef.current?.(resolving);
  }, [resolving]);

  const scheduleReverse = useCallback((lat, lng) => {
    clearTimeout(reverseTimerRef.current);
    const seq = reverseSeqRef.current + 1;
    reverseSeqRef.current = seq;
    const language = String(i18n.language || "en").toLowerCase().startsWith("ar") ? "ar" : "en";
    reverseTimerRef.current = setTimeout(async () => {
      try {
        const result = await reverseTrackHubLocation({ lat, lng, language });
        if (seq !== reverseSeqRef.current) return;
        if (!result?.full) return;
        onChangeRef.current({ address: result.full }, { source: "reverse" });
      } catch {
        // Keep the address already on the form when reverse geocoding fails.
      }
    }, REVERSE_DEBOUNCE_MS);
  }, [i18n.language]);

  useEffect(() => () => clearTimeout(reverseTimerRef.current), []);

  useEffect(() => {
    if (!mapFocus || mapFocus.token == null) return;
    const lat = Number(mapFocus.lat);
    const lng = Number(mapFocus.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    focusRef.current = { lat, lng, token: mapFocus.token };
    setMapTarget({
      lat,
      lng,
      zoom: mapFocus.zoom != null ? mapFocus.zoom : 18,
      token: mapFocus.token,
    });
  }, [mapFocus]);

  useEffect(() => {
    if (skipPanRef.current) {
      skipPanRef.current = false;
      return undefined;
    }
    const lat = parseCoord(latValue);
    const lng = parseCoord(lngValue);
    if (lat == null || lng == null || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
      return undefined;
    }
    const focused = focusRef.current;
    focusRef.current = null;
    if (focused && coordsMatch(focused.lat, focused.lng, lat, lng)) {
      return undefined;
    }
    const handle = setTimeout(() => {
      setMapTarget({ lat, lng, zoom: 18, token: Date.now() });
      scheduleReverse(lat, lng);
    }, 500);
    return () => clearTimeout(handle);
  }, [latValue, lngValue, scheduleReverse]);

  const handleQueryChange = (nextQuery) => {
    setLocationError(null);
    setResolveStatus(null);
    onChangeRef.current({ query: nextQuery }, { source: "input" });
  };

  const handleMapMove = useCallback((lat, lng) => {
    const latStr = Number(lat).toFixed(6);
    const lngStr = Number(lng).toFixed(6);
    if (String(latValue) === latStr && String(lngValue) === lngStr) {
      return;
    }
    skipPanRef.current = true;
    onChangeRef.current({ lat: latStr, lng: lngStr }, { source: "map" });
    scheduleReverse(Number(latStr), Number(lngStr));
  }, [latValue, lngValue, scheduleReverse]);

  const handleResolve = async () => {
    const input = String(query || "").trim();
    if (disabled) {
      return;
    }
    if (!input) {
      setLocationError(t("shipmentsPage.createManual.shortCodeRequired"));
      setResolveStatus(null);
      return;
    }
    setResolving(true);
    setLocationError(null);
    try {
      const result = await resolveTrackHubLocation({ input });
      const lat = Number(result?.lat);
      const lng = Number(result?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        throw new Error(result?.error || t("shipmentsPage.createManual.resolveError"));
      }
      const latStr = lat.toFixed(6);
      const lngStr = lng.toFixed(6);
      if (String(latValue) !== latStr || String(lngValue) !== lngStr) {
        skipPanRef.current = true;
      }
      onChangeRef.current(
        {
          lat: latStr,
          lng: lngStr,
          plusCode: result.plusCode || "",
          nationalShortCode: result.nationalShortCode || "",
          address: result.placeName || value?.address || `${latStr}, ${lngStr}`,
        },
        { source: "resolve" }
      );
      setMapTarget({ lat, lng, zoom: 18, token: Date.now() });
      setResolveStatus(
        [result.placeName, result.kind, result.precision, `${latStr}, ${lngStr}`]
          .filter(Boolean)
          .join(" · ")
      );
      if (result.kind !== "national-short-code") {
        scheduleReverse(lat, lng);
      }
    } catch (error) {
      const message = error?.response?.data?.error
        || error?.response?.data?.message
        || error?.message
        || t("shipmentsPage.createManual.resolveError");
      setLocationError(t("shipmentsPage.createManual.resolveFailed", { message }));
      setResolveStatus(null);
    } finally {
      setResolving(false);
    }
  };

  const headingText = heading || t("shipmentsPage.createManual.dropoffDetails");
  const labelText = label || t("shipmentsPage.createManual.nationalShortCode");
  const placeholderText = placeholder || t("shipmentsPage.createManual.shortCodePlaceholder");
  const hintText = hint || t("shipmentsPage.createManual.shortCodeHint");
  const mapHintText = mapHint || t("shipmentsPage.createManual.mapHint");
  const trimmedQuery = String(query || "").trim();

  return (
    <div className={`dlf${className ? ` ${className}` : ""}`}>
      {showHeading ? (
        <h3 className="dlf__title">
          <MapPin size={13} strokeWidth={2} />
          <span>{headingText}</span>
        </h3>
      ) : null}

      <div className="dlf__field">
        <label className="dlf__label" htmlFor={queryId}>
          {labelText}
        </label>
        <div className="dlf__resolve-row">
          <input
            id={queryId}
            className={`dlf__input${locationError ? " dlf__input--error" : ""}`}
            dir="ltr"
            placeholder={placeholderText}
            value={query}
            disabled={disabled}
            onChange={(e) => handleQueryChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleResolve();
              }
            }}
            aria-invalid={Boolean(locationError)}
          />
          <button
            type="button"
            className="dlf__resolve-btn"
            onClick={handleResolve}
            disabled={disabled || resolving}
          >
            {resolving
              ? t("shipmentsPage.createManual.resolving")
              : t("shipmentsPage.createManual.resolve")}
          </button>
        </div>
        {locationError ? (
          <p className="dlf__error">{locationError}</p>
        ) : resolveStatus || String(hintText || "").trim() ? (
          <p className="dlf__hint">{resolveStatus || hintText}</p>
        ) : null}
        {Array.isArray(chips) && chips.length ? (
          <div className="dlf__chips">
            {chips.map((chip) => (
              <button
                key={chip.id}
                type="button"
                className={[
                  "dlf__chip",
                  chip.highlight ? "dlf__chip--highlight" : "",
                  chip.badge ? "dlf__chip--badged" : "",
                ].filter(Boolean).join(" ")}
                disabled={disabled}
                onClick={() => {
                  handleQueryChange(chip.value);
                  onChip?.(chip);
                }}
              >
                {chip.badge ? <span className="dlf__chip-badge">{chip.badge}</span> : null}
                {chip.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {active ? (
        <DropoffLocationMap target={mapTarget} onUserMove={handleMapMove} />
      ) : null}
      <p className="dlf__hint dlf__map-hint">{mapHintText}</p>
    </div>
  );
}

export default DropoffLocationField;
